<template>
  <v-container fluid>
    <v-row>
      <v-col cols="0" lg="2" md="0"> </v-col>
      <v-col cols="12" lg="8" md="12">
        <v-card flat class="mx-auto">
          <v-card-title>
            Your Trade-In Quote
          </v-card-title>
          <p>
            We have emailed you a copy of this quote along with next steps to
            complete your trade-in.
          </p>
          <p>
            <b
              >If you do not receive this email, please remember to check your
              junk folder!</b
            >
          </p>
          <v-data-table
            :headers="headers"
            :items="cart_items"
            class="elevation-1"
            hide-default-footer
            dense
            ref="quoteTable"
          >
            <template v-slot:item.image="{ item }">
              <v-img :src="item.image" width="64"></v-img>
            </template>
            <template v-slot:item.title="{ item }">
              {{ item.title }}
              <p>
                <i> {{ item.description }} </i>
              </p>
            </template>
            <template v-slot:item.unit_price="{ item }">
              ${{ item.unit_price.toFixed(2) }}
            </template>
            <template v-slot:item.bonus="{ item }">
              <div v-if="item.bonus">+{{ item.bonus_percent.toFixed(0) }}%</div>
            </template>
            <template v-slot:item.subtotal="{ item }">
              ${{ item.subtotal.toFixed(2) }}
            </template>
            <template slot="body.append">
              <tr v-if="breakpoint_columns !== 0">
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td>Total:</td>
                <td>${{ cart_total.toFixed(2) }}</td>
              </tr>
              <tr v-else>
                <td>Total: ${{ cart_total.toFixed(2) }}</td>
              </tr>
            </template>
          </v-data-table>
          <v-card-text>
            <p>
              We will price match any other trade in value. Prices are subject
              to change any time and quotes are only valid for 30 days.
            </p>
            <p>We offer several convenient options to complete your trade:</p>
            <ul>
              <li>Book a curbside drop-off at our Barrhaven location.</li>
              <li>Have us email you a pre-paid shipping label.</li>
              <li>Get in-store credit equal to the value of your trade*.</li>
            </ul>
            <p></p>
            <p>
              *Note when purchasing an item using a trade in code, you can book
              an appointment to drop off your trade and pick up your purchase at
              the same time. Please purchase your item online prior to booking
              an appointment.
            </p>
          </v-card-text>
          <!-- show on screens size large or larger -->
          <div class="d-none d-lg-block">
            <v-card-actions>
              <v-btn
                large
                class="white--text"
                color="grey darken-2"
                :disabled="cart_empty"
                @click="$emit('add-more')"
              >
                <v-icon>mdi-keyboard-backspace</v-icon> &nbsp; Add more clubs
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                large
                class="white--text"
                color="green"
                href="https://sales.simplybook.me/v2/"
                target="_blank"
              >
                Book Drop-Off
              </v-btn>
              <v-btn
                large
                class="white--text"
                color="green"
                @click="getShippingLabel = true"
              >
                Get Shipping Label
              </v-btn>
              <v-btn
                large
                class="white--text"
                color="green"
                @click="generateCredit"
              >
                Get Store Credit
              </v-btn>
            </v-card-actions>
          </div>
          <!-- Show on screens smaller than large -->
          <div class="d-lg-none">
            <v-row>
              <v-btn
                large
                class="white--text action-button-spacing"
                color="green"
                href="https://sales.simplybook.me/v2/"
                target="_blank"
              >
                Book Drop-Off
              </v-btn>
            </v-row>
            <v-row>
              <v-btn
                large
                class="white--text action-button-spacing"
                color="green"
                @click="getShippingLabel = true"
              >
                Get Shipping Label
              </v-btn>
            </v-row>
            <v-row>
              <v-btn
                large
                class="white--text action-button-spacing"
                color="green"
                @click="generateCredit"
              >
                Get Store Credit
              </v-btn>
            </v-row>
            <v-row>
              <v-btn
                large
                class="white--text return-button-spacing"
                color="grey darken-2"
                :disabled="cart_empty"
                @click="$emit('add-more')"
              >
                <v-icon>mdi-keyboard-backspace</v-icon> &nbsp; Add more clubs
              </v-btn>
            </v-row>
          </div>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="0" lg="2" md="0"> </v-col>
      <v-col cols="12" lg="8" md="12">
        <v-alert
          type="success"
          :value="quoteSavedAlert"
          transition="fade-transition"
        >
          Thank you for your submission, a CGE representative will contact you
          with the next steps.
        </v-alert>
        <v-alert
          type="error"
          :value="quoteErrorAlert"
          transition="fade-transition"
        >
          Could not save quote! Please manually save a copy of this quote, and
          send it to sales@capitalgolfexchange.com to begin the trade in
          process.
        </v-alert>
      </v-col>
    </v-row>
    <v-overlay :value="getShippingLabel">
      <AddressCard
        :request_sent="labelRequestSaved"
        @close="getShippingLabel = false"
        @get-label="generateLabel"
      ></AddressCard>
    </v-overlay>
    <v-overlay :value="getCredit">
      <CreditNotify @close="getCredit = false"></CreditNotify>
    </v-overlay>
  </v-container>
</template>

<script>
import {
  quotesCollection,
  emailCollection,
  configCollection,
  addTimeStamp,
} from "@/firebase";
import firebase from "firebase/app";
import AddressCard from "./AddressCard";
import CreditNotify from "./CreditNotify";

export default {
  name: "Quote",
  components: {
    AddressCard,
    CreditNotify,
  },
  data() {
    return {
      headers: [
        { text: "", value: "image" },
        { text: "Model", value: "title" },
        { text: "Quantity", value: "quantity" },
        { text: "Price", value: "unit_price" },
        { text: "Bonus", value: "bonus" },
        { text: "Subtotal", value: "subtotal" },
      ],
      bonus_percent: 0,
      bonus_cutoff: 5,
      bonus_applied: false,
      quoteSaved: false,
      quoteErrorAlert: false,
      quoteSavedAlert: false,
      getShippingLabel: false,
      labelRequestSaved: false,
      getCredit: false,
      getCreditError: false,
      quoteID: "",
    };
  },
  created() {
    this.bonus_percent = this.$store.getters.getConfig[
      "Trade-In Bonus Percentage"
    ];
    this.bonus_cutoff = this.$store.getters.getConfig["Trade-In Bonus Cutoff"];
    this.bonus_applied = true;
    this.submit();
  },
  computed: {
    breakpoint_columns() {
      // datatable will eat columns depending on breakpoint
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 0;
        case "sm":
          return 4;
        case "md":
          return 4;
        case "lg":
          return 4;
        case "xl":
          return 4;
      }
      return 0;
    },
    cart_items() {
      return this.$store.getters.getCart.map(this.calc_bonus);
    },
    cart_empty() {
      return this.cart_items.length === 0;
    },
    cart_subtotal() {
      let sum = 0;
      this.cart_items.forEach((item) => {
        sum += item.subtotal;
      });
      return sum;
    },
    cart_total() {
      return this.cart_subtotal;
    },
    name() {
      return this.$store.getters.getCustomer.name;
    },
    email() {
      return this.$store.getters.getCustomer.email;
    },
    address() {
      return this.$store.getters.getAddress;
    },
    plainTextCreditRequest() {
      let text = `Discount code has been requested for the quote below.\r\n\r\n`;
      text += `Name: ${this.name}\r\n`;
      text += `Email: ${this.email}\r\n\r\n`;
      text += "Original Quote: \r\n";
      text += this.plaintextSummary;
      return text;
    },
    plainTextShippingLabel() {
      let text = `New shipping label requested for the quote below.\r\n\r\n`;
      text += `Name: ${this.name}\r\n`;
      text += `Email: ${this.email}\r\n`;
      text += `Address:\r\n`;
      text += `${this.address.address1}\r\n`;
      text += `${this.address.address2}\r\n`;
      text += `${this.address.city}, ${this.address.province}, ${this.address.postal_code}\r\n\r\n`;
      text += "Original Quote: \r\n";
      text += this.plaintextSummary;
      return text;
    },
    htmlCreditRequest() {
      let text = `Discount code has been requested for the quote below.<br/><br/>`;
      text += `Name: ${this.name}<br/>`;
      text += `Email: ${this.email}<br/><br/>`;
      text += "Original Quote: <br/>";
      text += this.htmlSummary;
      return text;
    },
    htmlShippingLabel() {
      let text = `New shipping label requested for the quote below.<br/><br/>`;
      text += `Name: ${this.name}<br/>`;
      text += `Email: ${this.email}<br/>`;
      text += `Address:<br/>`;
      text += `${this.address.address1}<br/>`;
      text += `${this.address.address2}<br/>`;
      text += `${this.address.city}, ${this.address.province}, ${this.address.postal_code}<br/><br/>`;
      text += "Original Quote: <br/>";
      text += this.htmlSummary;
      return text;
    },
    plaintextSummary() {
      let text = `Hi ${this.name}, \r\n\r\nThank you for visiting Capital Golf Exchange. Your trade-in quote summary is:\r\n\r\n`;
      text +=
        "Model \t Description \t Quantity \t Price \t Bonus \t Subtotal\r\n";
      this.cart_items.forEach((item) => {
        const bonusText = item.bonus
          ? `+${item.bonus_percent.toFixed(0)}%`
          : "";
        text += `${item.title} \t ${item.description} \t ${
          item.quantity
        } \t $${item.unit_price.toFixed(
          2
        )} \t ${bonusText} \t $${item.subtotal.toFixed(2)}\r\n`;
      });

      text += `Total Trade-In Value: $${this.cart_total.toFixed(2)}`;
      text +=
        "\r\n\r\nA Capital Golf Exchange representative will contact you soon with next steps to complete your trade-in.";

      return text;
    },
    htmlSummary() {
      let text = `Hi ${this.name}, <br/><br/>Thank you for visiting Capital Golf Exchange. Your trade-in quote summary is:<br/><br/>`;
      text += this.$refs.quoteTable.$el.outerHTML;
      text += "<br/><br/>";
      text += "We offer the following options for submitting your clubs:";

      text += "<br/><br/>";
      text += "Ottawa/Gatineau";
      text += "<ul>";
      text += "<li>Prepaid Shipping Label</li>";
      text +=
        "<li>Barrhaven Curbside Dropoff <a href='https://sales.simplybook.me/v2/'>(Book an Appointment)</a></li>";
      text += "</ul>";

      text += "<br/>";
      text += "Canada Wide";
      text += "<ul>";
      text += "<li>Prepaid Shipping Label</li>";
      text += "</ul>";

      text += "<br/><br/>";
      text +=
        "If you choose to ship us your clubs please provide your shipping address and we will send you a pre paid shipping label.";

      text += "<br/><br/>";
      text +=
        "Once the items are received we will pay you in cash (e-transfer) or store credit within 24 hours of receiving your item(s). If you wish to use as store credit towards a purchase please advise and we will send you a discount code equivalent to your quote to offset the purchase price. After purchasing the product online, you can <a href='https://sales.simplybook.me/v2/'>book an appointment</a> to pick up your item and drop off your trade at the same time.";

      text += "<br/><br/>";
      text += "Capital Golf Exchange Inc.";
      text += "<br/>";
      text += "1-855-673-GOLF (4653)";
      text += "<br/>";
      text +=
        "<a href='www.capitalgolfexchange.com'>www.capitalgolfexchange.com</a>";
      text += "<br/>";
      text +=
        "<a href='www.facebook.com/capitalgolfexchange'>www.facebook.com/capitalgolfexchange</a>";
      text += "<br/>";
      text +=
        "<a href='www.twitter.com/capitalgolfex'>www.twitter.com/capitalgolfex</a>";
      text += "<br/>";
      text +=
        "<a href='www.instagram.com/capital_golf_exchange'>www.instagram.com/capital_golf_exchange</a>";
      text += "<br/>";
      text +=
        "<a href='www.youtube.com/c/CapitalGolfExchange'>www.youtube.com/c/CapitalGolfExchange</a>";
      return text;
    },
  },
  methods: {
    submit() {
      const countersRef = configCollection.doc("counters");
      let quote_num = -1;

      countersRef
        .get()
        .then((res) => {
          quote_num = res.data().quotes;

          const data = {
            name: this.name,
            email: this.email,
            subtotal: this.cart_subtotal,
            total: this.cart_total,
            items: this.cart_items,
            timestamp: addTimeStamp(),
            quote_number: quote_num,
          };

          return quotesCollection.add(data);
        })
        .then(() => {
          this.generateEmail(quote_num);
          countersRef.update({
            quotes: firebase.firestore.FieldValue.increment(1),
          });
        })
        .catch((err) => {
          console.log(err);
          this.quoteErrorAlert = true;
        });
    },
    clear() {
      this.$v.$reset();
      this.name = "";
      this.email = "";
    },
    generateCredit() {
      const data = {
        to: ["sales@capitalgolfexchange.com"],
        message: {
          subject: `Discount Code Requested for <CGETRD-${this.quoteID}>`,
          text: this.plainTextCreditRequest,
          html: this.htmlCreditRequest,
        },
      };
      emailCollection
        .add(data)
        .then(() => {
          this.getCredit = true;
        })
        .catch((err) => {
          this.getCreditError = true;
          console.log(err);
        });
    },
    generateLabel() {
      // this.getShippingLabel = false;
      const data = {
        to: ["sales@capitalgolfexchange.com"],
        message: {
          subject: `Shipping Label Requested for <CGETRD-${this.quoteID}>`,
          text: this.plainTextShippingLabel,
          html: this.htmlShippingLabel,
        },
      };
      emailCollection
        .add(data)
        .then(() => {
          this.labelRequestSaved = true;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    generateEmail(id) {
      const data = {
        to: this.email,
        cc: ["sales@capitalgolfexchange.com"],
        message: {
          subject: `Capital Golf Exchange Trade In Quote <CGETRD-${id}>`,
          text: this.plaintextSummary,
          html: this.htmlSummary,
        },
      };
      // Disable sending email while testing
      emailCollection
        .add(data)
        .then(() => {
          this.quoteID = id;
          this.quoteSaved = true;
          // this.quoteSavedAlert = true;
          // setTimeout(() => { this.quoteSavedAlert = false }, 5000);
        })
        .catch((err) => {
          this.quoteErrorAlert = true;
          console.log(err);
        });
      // return data;
    },
    calc_bonus(item) {
      if (item.condition >= this.bonus_cutoff) {
        return {
          ...item,
          bonus: true,
          bonus_percent: this.bonus_percent,
          bonus_price: (1 + this.bonus_percent / 100) * item.price,
          subtotal: (1 + this.bonus_percent / 100) * item.subtotal,
        };
      } else {
        return { ...item, bonus: false };
      }
    },
  },
};
</script>

<style scoped>
.action-button-spacing {
  margin: 5px;
  margin-left: 30px;
}
.return-button-spacing {
  margin: 20px;
  margin-left: 30px;
}
.summary-text {
  border: 2px;
  white-space: nowrap;
  overflow: hidden;
  width: 180px;
  text-align: right;
  font-family: "Work Sans";
  color: 69727b;
}
</style>
